import { downloadZip, makeZip } from 'client-zip';
import { saveAs } from 'file-saver';
import { DownloadSignedUrlsProgress, SignedUrlEntry } from '@/types/download';
import {
  createCoalescedProgressHandler,
  createSignedUrlEntryGenerator,
  ZipDownloadCoreState,
  ZipDownloadProgressEvent,
  ZipEntry,
  ZipDownloadAbortedError,
} from '@/lib/download/zip-download-core';

function toPublicProgress(progress: ZipDownloadProgressEvent): DownloadSignedUrlsProgress {
  return {
    downloaded: progress.downloaded,
    failed: progress.failed,
    total: progress.total,
    phase: progress.phase,
    bytesReceived: progress.bytesReceived,
    totalBytes: progress.totalBytes,
    percent: progress.percent,
  };
}

async function writeZipToDisk(
  generator: AsyncGenerator<ZipEntry>,
  saveHandle: FileSystemFileHandle,
): Promise<void> {
  const writable = await saveHandle.createWritable();
  try {
    await makeZip(generator).pipeTo(writable);
  } catch (error) {
    try {
      await writable.abort();
    } catch {
      return;
    }
    throw error;
  }
}

async function writeZipAsBlobDownload(
  generator: AsyncGenerator<ZipEntry>,
  zipName: string,
): Promise<void> {
  const blob = await downloadZip(generator).blob();
  saveAs(blob, `${zipName}.zip`);
}

export async function streamSignedUrlsToZip(
  signedUrls: SignedUrlEntry[],
  zipName: string,
  saveHandle: FileSystemFileHandle | null | undefined,
  // eslint-disable-next-line no-unused-vars
  onProgress?: (progress: DownloadSignedUrlsProgress) => void,
  signal?: AbortSignal,
): Promise<{ downloaded: number; failedCount: number }> {
  const abortController = new AbortController();
  const abortDownload = () => abortController.abort();
  if (signal?.aborted) {
    throw new ZipDownloadAbortedError();
  }
  signal?.addEventListener('abort', abortDownload, { once: true });
  const state: ZipDownloadCoreState = {
    downloaded: 0,
    failedCount: 0,
    total: 0,
    bytesReceived: 0,
  };

  const handleProgress = createCoalescedProgressHandler((progress) => {
    onProgress?.(toPublicProgress(progress));
  });

  const generator = createSignedUrlEntryGenerator(
    signedUrls,
    state,
    abortController,
    handleProgress,
  );

  try {
    if (saveHandle) {
      await writeZipToDisk(generator, saveHandle);
    } else {
      await writeZipAsBlobDownload(generator, zipName);
    }

    if (abortController.signal.aborted) {
      throw new ZipDownloadAbortedError();
    }

    if (state.downloaded === 0) {
      throw new Error('No se pudo descargar ninguna imagen');
    }

    return { downloaded: state.downloaded, failedCount: state.failedCount };
  } finally {
    signal?.removeEventListener('abort', abortDownload);
  }
}
